// @ts-check
// src/components/reactive.js — Faza D.3.4 lit-html + Nanostores integration
//
// Helpers do reaktywnego renderowania komponentów lit-html, gdy Nanostore się zmieni.
//
// API:
//   const unsub = bindStore(container, store, (storeValue) => html`...template...`);
//   unsub();  // cleanup
//
//   bindStores(container, [store1, store2], ([v1, v2]) => html`...`);

import { render } from 'lit-html';

/**
 * Bind 1 store do containera. Re-renderuje template przy każdej zmianie store.
 *
 * @param {HTMLElement} container - DOM element gdzie render
 * @param {Object} store - Nanostores atom (musi mieć .subscribe + .get)
 * @param {(value: *) => import('lit-html').TemplateResult} templateFn - funkcja zwracająca lit-html template
 * @returns {() => void} unsubscribe function
 */
export function bindStore(container, store, templateFn) {
  const _render = (value) => {
    try {
      render(templateFn(value), container);
    } catch (e) {
      console.error('[reactive/bindStore] render failed:', e);
    }
  };
  // Initial render + subscribe future changes
  return store.subscribe(_render);
}

/**
 * Bind N stores do containera. Re-render gdy ktorystkolwiek się zmieni.
 *
 * @param {HTMLElement} container
 * @param {Object[]} stores - lista store'ów
 * @param {(values: any[]) => import('lit-html').TemplateResult} templateFn
 * @returns {() => void} unsubscribe
 */
export function bindStores(container, stores, templateFn) {
  const _render = () => {
    try {
      const values = stores.map((s) => s.get());
      render(templateFn(values), container);
    } catch (e) {
      console.error('[reactive/bindStores] render failed:', e);
    }
  };
  const unsubs = stores.map((s) => s.subscribe(_render));
  return () => unsubs.forEach((u) => u());
}

/**
 * Helper: re-render gdy callback zwróci `true` przy każdej zmianie store.
 * Useful dla komponentów które mają own internal state niż Nanostores.
 *
 * @param {HTMLElement} container
 * @param {() => import('lit-html').TemplateResult} templateFn
 * @param {Object[]} dependencies - stores do subscribe
 * @returns {() => void} unsubscribe
 */
export function bindReactive(container, templateFn, dependencies = []) {
  const _render = () => render(templateFn(), container);
  _render(); // initial
  const unsubs = dependencies.map((s) => s.subscribe(_render));
  return () => unsubs.forEach((u) => u());
}
